import clsx from 'clsx';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import style from './RowLayoutPagination.module.css';

const SHOW_FULL_PAGE_LAYOUT_MAX_PAGES = 5;
const MAX_NUMBER_OF_LARGE_DOTS = SHOW_FULL_PAGE_LAYOUT_MAX_PAGES - 2;
const MAX_NON_LARGE_DOTS_PER_SIDE = 2;

export interface RowLayoutPaginationProps {
  pages: number[];
  activePage: number;
  onClick: ((indicatorId: string) => void) | undefined;
}

type RowLayoutDirection = 'next' | 'previous';
type RowLayoutMainIndexPosition = 'left' | 'center' | 'right';
type LargeDotIndexes = [start: number, end: number];

export const RowLayoutPagination = ({ pages, activePage, onClick }: RowLayoutPaginationProps) => {
  const numberOfPages = pages.length;

  const scalePages = numberOfPages > SHOW_FULL_PAGE_LAYOUT_MAX_PAGES;
  const indexOfCurrentPage = pages.indexOf(activePage);
  const [lastActivePageIndex, setLastActivePageIndex] = useState<number | undefined>(undefined);
  const [direction, setDirection] = useState<RowLayoutDirection | undefined>(undefined);
  const [largeDotPageIndexes, setLargeDotPageIndexes] = useState<LargeDotIndexes | undefined>(undefined);
  const [mainIndexPosition, setMainIndexPosition] = useState<RowLayoutMainIndexPosition>('left');
  const [didChangeIndex, setDidChangeIndex] = useState<boolean>(false);

  useLayoutEffect(() => {
    setLastActivePageIndex(typeof activePage === 'number' ? activePage : parseInt(activePage, 10));
    if (lastActivePageIndex !== activePage) {
      const newDirection: RowLayoutDirection = !lastActivePageIndex || lastActivePageIndex < activePage ? 'next' : 'previous';
      setDirection(newDirection);
      setDidChangeIndex(lastActivePageIndex !== activePage);
    }
  }, [activePage]);

  const getNextMainIndexPosition = useCallback((position: RowLayoutMainIndexPosition) => {
    switch (position) {
      case 'left':
        return 'center';
      case 'center':
      case 'right':
        return 'right';
    }
    throw new Error(`Unhandled ${position}`);
  }, []);

  const getPreviousMainIndexPosition = useCallback((position: RowLayoutMainIndexPosition) => {
    switch (position) {
      case 'right':
        return 'center';
      case 'center':
      case 'left':
        return 'left';
    }
    throw new Error(`Unhandled ${position}`);
  }, []);

  const getNewMainIndexPosition = useCallback(() => {
    if (lastActivePageIndex === 0 || lastActivePageIndex === undefined) {
      return 'left';
    }
    const maxIndex = numberOfPages - 1;
    if (lastActivePageIndex === maxIndex) {
      return 'right';
    }

    switch (direction) {
      case 'next':
        return getNextMainIndexPosition(mainIndexPosition);
      case 'previous':
        return getPreviousMainIndexPosition(mainIndexPosition);
    }
    throw new Error(`Unhandled ${direction}`);
  }, [direction, getNextMainIndexPosition, getPreviousMainIndexPosition, lastActivePageIndex, mainIndexPosition, numberOfPages]);

  useLayoutEffect(() => {
    if (!didChangeIndex) {
      return;
    }

    const newMainIndex = getNewMainIndexPosition();
    setMainIndexPosition(newMainIndex);
    setDidChangeIndex(false);
  }, [didChangeIndex, getNewMainIndexPosition]);

  const getLargeDotsStartIdx = useCallback((mainIndex: number, indexPosition: RowLayoutMainIndexPosition) => {
    switch (indexPosition) {
      case 'left':
        return mainIndex;
      case 'center':
        return Math.max(mainIndex - 1, 0);
      case 'right':
        return Math.max(mainIndex - 2, 0);
    }
    throw new Error(`Unhandled mainIndexPos: ${indexPosition}`);
  }, []);

  const getLargeDotsEndIdx = useCallback((largeDotsStartIdx: number, numberOfPages: number) => {
    return Math.min(largeDotsStartIdx + MAX_NUMBER_OF_LARGE_DOTS - 1, numberOfPages - 1);
  }, []);

  const calculateLargeDotIndexes = useCallback(
    (mainIndex: number, currentLargeDotIndexes: LargeDotIndexes | undefined, numberOfVisiblePages: number): LargeDotIndexes => {
      const newStartIdx = getLargeDotsStartIdx(mainIndex, mainIndexPosition);
      const newEndIdx = getLargeDotsEndIdx(newStartIdx, numberOfVisiblePages);

      return [newStartIdx, newEndIdx];
    },
    [getLargeDotsEndIdx, getLargeDotsStartIdx, mainIndexPosition]
  );

  const visiblePages = useMemo(() => {
    if (!scalePages) {
      return [...pages];
    }

    // find out what pages to show
    const largeDotsStartIdx = getLargeDotsStartIdx(indexOfCurrentPage, mainIndexPosition);
    const largeDotsEndIdx = getLargeDotsEndIdx(largeDotsStartIdx, numberOfPages);

    // first look left
    const minLeftIndex = Math.max(largeDotsStartIdx - MAX_NON_LARGE_DOTS_PER_SIDE, 0);
    const visiblePages = pages.slice(minLeftIndex, indexOfCurrentPage + 1);
    const maxIndex = Math.min(numberOfPages - 1, largeDotsEndIdx + MAX_NON_LARGE_DOTS_PER_SIDE);
    if (maxIndex > indexOfCurrentPage) {
      visiblePages.push(...pages.slice(indexOfCurrentPage + 1, maxIndex + 1));
    }
    return visiblePages;
  }, [getLargeDotsEndIdx, getLargeDotsStartIdx, indexOfCurrentPage, mainIndexPosition, numberOfPages, pages, scalePages]);

  const indexOfCurrentVisiblePage = visiblePages.indexOf(activePage);

  const requiresLargeDotIndexUpdate = useCallback((oldIndexes: LargeDotIndexes | undefined, newIndexes: LargeDotIndexes) => {
    return !oldIndexes || oldIndexes[0] !== newIndexes[0] || oldIndexes[1] !== newIndexes[1];
  }, []);

  const newLargeDotPageIndexes = calculateLargeDotIndexes(indexOfCurrentVisiblePage, largeDotPageIndexes, visiblePages.length);
  const [newLargeDotStart, newLargeDotEnd] = newLargeDotPageIndexes;

  if (requiresLargeDotIndexUpdate(largeDotPageIndexes, newLargeDotPageIndexes)) {
    setLargeDotPageIndexes(newLargeDotPageIndexes);
  }

  const onDotClick = useCallback(
    (pageID: number) => () => {
      onClick?.(`${pageID}`);
    },
    [onClick]
  );

  const finalPagesMarkup = visiblePages.map((pageId, index) => {
    const isActivePage = pageId === activePage;
    const isLarge = !scalePages || (index >= newLargeDotStart && index <= newLargeDotEnd);
    const isSmall = scalePages && !isLarge && index >= newLargeDotStart - 1 && index <= newLargeDotEnd + 1;
    const isTiny = scalePages && !isSmall && !isLarge;
    const id = `row-layout-pagination-${pageId}`;
    return (
      <button
        id={id}
        key={id}
        className={clsx(style.dot, { [style.active]: isActivePage, [style.large]: isLarge, [style.small]: isSmall, [style.tiny]: isTiny })}
        tabIndex={isActivePage ? -1 : 0}
        type="button"
        onClick={onDotClick(pageId)}
        aria-label={`page ${index + 1}`}
      />
    );
  });

  return <div className={clsx(style.root)}>{finalPagesMarkup}</div>;
};
